import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../services/adminApi";

function StatCard({ label, value, color = "blue" }) {
  const colors = { blue: "bg-blue-50 text-blue-700", green: "bg-green-50 text-green-700", amber: "bg-amber-50 text-amber-700", red: "bg-red-50 text-red-700" };
  return (
    <div className={`rounded-xl p-5 ${colors[color]}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm font-medium mt-0.5 opacity-80">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const [drivers, setDrivers] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminApi.listDrivers(),
      adminApi.getCompliance(),
      adminApi.listMessages(),
    ]).then(([d, c, m]) => {
      setDrivers(d.drivers || []);
      setCompliance(c);
      setMessages(m.messages || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  const activeDrivers = drivers.filter((d) => d.status === "active").length;
  const completedEnrollment = drivers.filter((d) => d.enrollmentStatus === "completed").length;
  const expiredItems = compliance?.items?.filter((i) => i.bucket === "expired").length || 0;
  const expiringSoon = compliance?.items?.filter((i) => i.bucket === "30_days").length || 0;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Active Drivers" value={activeDrivers} color="blue" />
        <StatCard label="Enrollment Complete" value={completedEnrollment} color="green" />
        <StatCard label="Items Expiring Soon" value={expiringSoon} color="amber" />
        <StatCard label="Expired Items" value={expiredItems} color="red" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent drivers */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Recent Drivers</h2>
            <Link to="/enrollme/admin/drivers" className="text-sm text-blue-600 hover:underline">View all</Link>
          </div>
          <ul className="divide-y divide-gray-100">
            {drivers.slice(0, 5).map((d) => (
              <li key={d._id}>
                <Link to={`/enrollme/admin/drivers/${d._id}`} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{d.firstName} {d.lastName}</p>
                    <p className="text-xs text-gray-500">{d.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${d.enrollmentStatus === "completed" ? "bg-green-100 text-green-700" : d.enrollmentStatus === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>
                    {(d.enrollmentStatus || "not_started").replace(/_/g, " ")}
                  </span>
                </Link>
              </li>
            ))}
            {drivers.length === 0 && <li className="px-5 py-6 text-sm text-gray-400 text-center">No drivers yet.</li>}
          </ul>
        </div>

        {/* Expiring items */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Expiring Items</h2>
            <Link to="/enrollme/admin/compliance" className="text-sm text-blue-600 hover:underline">View all</Link>
          </div>
          <ul className="divide-y divide-gray-100">
            {(compliance?.items || []).slice(0, 5).map((item) => (
              <li key={item._id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.ownerName}</p>
                </div>
                <span className={`text-xs font-medium ${item.daysLeft < 0 ? "text-red-600" : item.daysLeft <= 7 ? "text-amber-600" : "text-blue-600"}`}>
                  {item.daysLeft < 0 ? `${Math.abs(item.daysLeft)}d overdue` : `${item.daysLeft}d left`}
                </span>
              </li>
            ))}
            {(!compliance?.items?.length) && <li className="px-5 py-6 text-sm text-gray-400 text-center">No expiring items.</li>}
          </ul>
        </div>
      </div>

      {/* Recent messages */}
      {messages.length > 0 && (
        <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Recent Messages</h2>
            <Link to="/enrollme/admin/messages" className="text-sm text-blue-600 hover:underline">View all</Link>
          </div>
          <ul className="divide-y divide-gray-100">
            {messages.slice(0, 3).map((m) => (
              <li key={m._id} className="px-5 py-3">
                <p className="text-sm font-medium text-gray-900">{m.subject}</p>
                <p className="text-xs text-gray-500">{m.senderName} · {new Date(m.createdAt).toLocaleDateString()}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
